import React, {useEffect, useMemo, useState} from 'react';

import omit from 'lodash.omit';
import {HeaderBlock, UpdateChangeRequest, UpdateChangeRequestType} from 'src/modules/event-create/typedefs';
import {getSourcesByEventType} from 'src/modules/event-create/utils/getSourcesByEventType';
import {getEventsInfo} from 'src/modules/events/services';
import {UsersInfoController} from 'src/modules/users-info/users-info.controller';
import {ChangeRequestContextualLink} from 'src/openapi/models/ChangeRequestContextualLink';
import {DecorationType} from 'src/openapi/models/DecorationType';
import {EventChangeRequest} from 'src/openapi/models/EventChangeRequest';
import {EventType} from 'src/openapi/models/EventType';
import {useAppSelector} from 'src/store';

import {Icons} from '@pb/kit-component-icon';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';

import {EventCreationMode, FIELDS_PATTERN, FIELDS_PATTERN_VALIDATION_MESSAGE, HeaderFields} from '../constants';

import {HeaderBlockView} from './header-block.view';

type Props = {
    eventCodeInfo: EventChangeRequest,
    eventChangeRequest: EventChangeRequest,
    defaultDueDate: Date | undefined,
    mode: EventCreationMode,
    validationHighLight: boolean,
    changeRequestId: number | null,
    onChangeContextualLinks: (value: ChangeRequestContextualLink[]) => void,
    onUpdateChangeRequest: (value: UpdateChangeRequest, type: UpdateChangeRequestType) => void,
    onChangeDueDate: (date?: Date) => void,
    onChangeDecorationType: (value: DecorationType | null) => void,
};

const AUTO_HIDE_TIMEOUT = 10000;

export const HeaderBlockController = (props: Props) => {
    const {
        eventCodeInfo,
        eventChangeRequest,
        defaultDueDate,
        mode,
        validationHighLight,
        changeRequestId,
        onChangeContextualLinks,
        onUpdateChangeRequest,
        onChangeDueDate,
        onChangeDecorationType,
    } = props;

    const {onCreateNote} = usePushNotification();
    const [headerValues, setHeaderValues] = useState<HeaderBlock>(omit(eventCodeInfo, 'attributes'));
    const eventsInfo = useAppSelector(getEventsInfo);
    const typesList = useMemo(() => Object.values(EventType).sort(), []);
    const sources = useMemo(() =>
        getSourcesByEventType(headerValues.type), [headerValues.type]);

    useEffect(() => {
        setHeaderValues(omit(eventCodeInfo, 'attributes'));
    }, [mode, eventCodeInfo]);

    useEffect(() => {
        if (headerValues.description.match(FIELDS_PATTERN.description)) {
            onCreateNote({
                id: 'event-description-warning',
                status: Status.WARNING,
                message: FIELDS_PATTERN_VALIDATION_MESSAGE,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: AUTO_HIDE_TIMEOUT,
            });

            setHeaderValues({
                ...headerValues,
                [HeaderFields.eventDescription]: headerValues.description.trim().replace(FIELDS_PATTERN.description, ''),
            });
            return;
        }

        if (headerValues.trigger.match(FIELDS_PATTERN.trigger)) {
            onCreateNote({
                id: 'event-trigger-warning',
                status: Status.WARNING,
                message: FIELDS_PATTERN_VALIDATION_MESSAGE,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: AUTO_HIDE_TIMEOUT,
            });

            setHeaderValues({
                ...headerValues,
                [HeaderFields.trigger]: headerValues.trigger.trim().replace(FIELDS_PATTERN.trigger, ''),
            });
            return;
        }

        onUpdateChangeRequest(omit(headerValues, 'attributes'), UpdateChangeRequestType.HEADER);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [headerValues, eventCodeInfo, eventsInfo, onCreateNote]);

    const onInputBlur = (event: React.FocusEvent<HTMLInputElement>, name?: string) => {
        if (!name) {
            return;
        }
        const value = event.target.value;
        setHeaderValues({...headerValues, [name]: value.trim()});
    };

    const onTriggerBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        setHeaderValues({...headerValues, [HeaderFields.trigger]: value.trim()});
    };

    const onChangeCheckboxField = (data: {event: React.SyntheticEvent<HTMLInputElement, Event>, name: string}) => {
        setHeaderValues({...headerValues, [data.name]: data.event.currentTarget.checked});
    };

    const onChangeField = (value: string, name?: string) => {
        if (!name) {
            return;
        }

        if (name === HeaderFields.eventType) {
            const allowedSources = getSourcesByEventType(value as EventType);
            const hasSource = allowedSources.includes(headerValues.source);

            setHeaderValues({
                ...headerValues,
                [HeaderFields.eventType]: value as EventType,
                [HeaderFields.eventSource]: hasSource ? headerValues.source : '',
            });
            return;
        }

        setHeaderValues({...headerValues, [name]: value});
    };

    const onChangeFamilies = (value: string[]) => {
        setHeaderValues({...headerValues, [HeaderFields.families]: value});
    };

    return (
        <>
            <UsersInfoController {...{changeRequestId}}/>
            <HeaderBlockView {...{
                typesList,
                sources,
                headerValues,
                mode,
                validationHighLight,
                changeRequestId,
                eventChangeRequest,
                defaultDueDate,
                onChangeContextualLinks,
                onInputBlur,
                onTriggerBlur,
                onChangeCheckboxField,
                onChangeField,
                onChangeFamilies,
                onChangeDueDate,
                onChangeDecorationType,
            }}
            />
        </>
    );
};
